import { SkyBadge, SkyDataGrid, SkyText, type SkyDataGridColumn } from "@skypointcloud/skypoint-ui";
import {
  CHECK_KIND_LABELS,
  INCIDENT_CLOSE_REASON_TEXT,
  type MonitorIncidentRow,
} from "@shared/types/monitor";
import { LIST_GRID_PAGINATION } from "@/lib/grid";
import { countWithBasis } from "@/lib/monitor/format";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * The Incidents tab (spec section 11): the open incidents, failures first, with what they are
 * about, when they opened, and who has taken each. Read-only in this phase: "Assign to me" and
 * "Not an issue (7 days)" arrive with the page's controls (phase 2); their routes already exist.
 *
 * Round 7 (owner 2026-10-03): with `closed`, the incidents closed in the last 30 days, each with
 * when it closed and why (`closeReasonText`): a check that can no longer judge says why in plain
 * words, a recovery and "Not an issue" say theirs.
 */
export function MonitorIncidentsGrid({
  rows,
  closed = false,
}: {
  rows: MonitorIncidentRow[];
  closed?: boolean;
}) {
  const zone = useDisplayTz();
  const columns: SkyDataGridColumn<MonitorIncidentRow>[] = [
    {
      key: "severity",
      header: "Severity",
      width: "10%",
      renderCell: (_value, row) => (
        <SkyBadge
          appearance="tint"
          color={row.severity === "failure" ? "danger" : "warning"}
          testId={`monitor-incident-severity-${row.id}`}
        >
          {row.severity === "failure" ? "Failing" : "Warning"}
        </SkyBadge>
      ),
    },
    {
      key: "entityName",
      header: "About",
      width: "30%",
      renderCell: (_value, row) => {
        const about =
          row.scheduleName !== null
            ? `Schedule ${row.scheduleName}`
            : // Round 6 W15: the warehouse table as the load named it, when it landed.
              `${row.connectionName ?? "Deleted connection"}: ${row.tableName ?? row.entityName ?? ""}`;
        return (
          <SkyText size={300} block truncate title={about}>
            {about}
          </SkyText>
        );
      },
    },
    {
      key: "kind",
      header: "Monitor",
      width: "14%",
      renderCell: (_value, row) => <SkyText size={300}>{CHECK_KIND_LABELS[row.kind]}</SkyText>,
    },
    {
      key: "lastValue",
      header: "Found",
      width: "14%",
      renderCell: (_value, row) => (
        // #1281: as the Tables tab, the rows the last load brought or the rows in total.
        <SkyText size={300} font="numeric">
          {countWithBasis(row.lastValue, row.unit, row.basis, "last")}
        </SkyText>
      ),
    },
    {
      key: "openedAt",
      header: "Opened",
      width: "16%",
      sortable: true,
      renderCell: (_value, row) => (
        <SkyText size={300} font="numeric" title={instantHoverTitle(row.openedAt, zone)}>
          {formatInstant(row.openedAt, zone, "minutes")}
        </SkyText>
      ),
    },
    {
      key: "status",
      header: "Taken by",
      width: "16%",
      renderCell: (_value, row) => (
        <SkyText size={300} color={row.acknowledgedBy ? undefined : "muted"}>
          {takenByWords(row)}
        </SkyText>
      ),
    },
  ];
  if (closed) {
    return (
      <SkyDataGrid<MonitorIncidentRow>
        data={rows}
        columns={closedColumns(columns, zone)}
        getRowId={(row) => row.id}
        {...LIST_GRID_PAGINATION}
        ariaLabel="Incidents closed in the last 30 days"
        emptyMessage="No incident closed in the last 30 days."
        testId="monitor-closed-incidents-grid"
      />
    );
  }
  return (
    <SkyDataGrid<MonitorIncidentRow>
      data={rows}
      columns={columns}
      getRowId={(row) => row.id}
      {...LIST_GRID_PAGINATION}
      ariaLabel="Open incidents"
      emptyMessage="No open incident."
      testId="monitor-incidents-grid"
    />
  );
}

function takenByWords(row: MonitorIncidentRow): string {
  if (row.acknowledgedByMe) return "You";
  return row.acknowledgedBy ? "Someone" : "Nobody yet";
}

/** Round 7: why a closed incident closed, in plain words. */
function closeReasonText(row: MonitorIncidentRow): string {
  // A person's choice, not something skyData lost: said as it is.
  if (row.closeReason === "check_turned_off" || row.closeReason === "marked_not_an_issue") {
    return INCIDENT_CLOSE_REASON_TEXT[row.closeReason];
  }
  if (row.closeReason) {
    const reason = INCIDENT_CLOSE_REASON_TEXT[row.closeReason];
    return `skyData can no longer check it: ${reason.charAt(0).toLowerCase()}${reason.slice(1)}`;
  }
  if (row.status === "dismissed") return "Not an issue";
  return "Recovered, or its monitor was turned off";
}

/**
 * The closed list's columns: what it was about, its monitor, when it opened and closed, and why.
 * "Found" and "Taken by" are about an open incident and are left out.
 */
function closedColumns(
  open: SkyDataGridColumn<MonitorIncidentRow>[],
  zone: ReturnType<typeof useDisplayTz>,
): SkyDataGridColumn<MonitorIncidentRow>[] {
  const keep = (key: string, width: string) => {
    const column = open.find((candidate) => candidate.key === key)!;
    return { ...column, width };
  };
  return [
    keep("severity", "10%"),
    keep("entityName", "24%"),
    keep("kind", "12%"),
    keep("openedAt", "14%"),
    {
      key: "closedAt",
      header: "Closed",
      width: "14%",
      renderCell: (_value, row) =>
        row.closedAt ? (
          <SkyText size={300} font="numeric" title={instantHoverTitle(row.closedAt, zone)}>
            {formatInstant(row.closedAt, zone, "minutes")}
          </SkyText>
        ) : null,
    },
    {
      key: "closeReason",
      header: "Why it closed",
      width: "26%",
      renderCell: (_value, row) => (
        <SkyText size={300} block testId={`monitor-incident-close-reason-${row.id}`}>
          {closeReasonText(row)}
        </SkyText>
      ),
    },
  ];
}
